import { socket } from "./socket";
import * as mediasoupClient from "mediasoup-client";
import { upsertParticipant } from "../services/web-RTC";
import { getTurnServers } from "../datas.config";

// DÉBUT DES VARIABLES GLOBALES
let device;
let isIntentionalLeave = false;
// FIN DES VARIABLES GLOBALES

export const setIntentionalLeave = (value) => {
  isIntentionalLeave = value;
};

export const isIntentionalLeaveActive = () => isIntentionalLeave;

export const leaveMeeting = (data) => {
  const {
    userId,
    endMeetingSession,
    setSendTransport,
    setRecvTransport,
    setProducerAudio,
    setProducerVideo,
    setIsScreenSharing,
    setScreenStream,
    sendTransport,
    recvTransport,
    producerVideo,
    producerAudio,
    localStream,
    screenStream,
    location,
  } = data;
  
  // Signaler au serveur que c'est une sortie intentionnelle
  socket.emit("intentional_leave");
  isIntentionalLeave = true;
  
  socket.emit("leave_meeting", userId, (res) => {
    if (res) {
      cleanupMediasoupConnection({
        sendTransport,
        recvTransport,
        producerVideo,
        producerAudio,
        localStream,
        screenStream,
      });
      setIsScreenSharing(false);
      setScreenStream(null);
      setSendTransport(null);
      setRecvTransport(null);
      setProducerAudio(null);
      setProducerVideo(null);
      endMeetingSession(true); // true = intentional
      // Navigation SPA si possible (pas de rechargement complet → pas de page blanche)
      if (typeof location === "function") {
        location("/reunion");
      } else {
        window.location.href = "/reunion";
      }
    } else {
      console.warn("Erreur serveur lors de la sortie de la réunion");
    }
  });
};

export const cleanupMediasoupConnection = async (data) => {
  const {
    sendTransport,
    recvTransport,
    producerVideo,
    producerAudio,
    localStream,
    screenStream,
  } = data;

  // 1️⃣ Fermer les producers (audio + vidéo + screen share éventuel)
  [producerAudio, producerVideo].forEach((producer) => {
    try {
      if (producer) {
        producer.close();
        producer.track.stop();
      }
    } catch (err) {
      console.warn("Erreur fermeture producer:", err);
    }
  });

  // 2️⃣  Fermer les transports
  if (sendTransport) {
    sendTransport.close();
  }
  if (recvTransport) {
    recvTransport.close();
  }

  // 3️⃣ Stopper la capture locale (libérer caméra / micro)
  if (screenStream) screenStream.getTracks().forEach((track) => track.stop());
  if (localStream) {
    localStream.getTracks().forEach((track) => {
      track.stop();
    });
  }
};

export const generateCodeAndMeeting = (user) => {
  return new Promise((resolve, reject) => {
    socket.emit("generate_code_meeting", user, (response) => {
      if (!response) {
        return reject(new Error("Aucune réponse du serveur"));
      }
      if (response.error === "already_in_meeting") {
        return reject(new Error(response.message));
      }
      resolve(response);
    });
  });
};

export const createAllWebTRCTransports = (user, datas) => {
  // datas contient {meetingId, accessCode,setMeetingId,setMessageJoinBoolean}
  let meetingId = datas.meetingId;
  let accessCode = datas.accessCode;
  const { setMessageJoinBoolean } = datas;
  return new Promise(async (resolve, reject) => {
    // 1) Si pas de meetingId, on rejoint d'abord et on ATTEND le callback
    if (!datas.meetingId) {
      const { setCode, setMeetingId } = datas;
      try {
        const res = await new Promise((res, rej) => {
          socket.emit(
            "je_rejoins_la_reunion",
            { accessCode: datas.accessCode, user },
            (response) => {
              if (
                response.error === "full" ||
                response.error === "already_in_meeting" ||
                response.error === "expired"
              ) {
                return rej(new Error(response.message));
              }
              res(response);
            }
          );
        });
        setMeetingId(res.meetingId);
        if (res.display) setCode(accessCode || res.accessCode);
        accessCode = res.accessCode;
        meetingId = res.meetingId;
        setMessageJoinBoolean(true);
      } catch (err) {
        return reject(err);
      }
    }

    // 2) Maintenant que les listeners sont enregistrés côté serveur, on crée les transports
    socket.emit(
      "create_all_webRTC_transports",
      { user, datas: { accessCode, meetingId } },
      (response) => {
        if (!response) {
          return reject(new Error("Aucune réponse du serveur"));
        }
        resolve(response);
      }
    );
  });
};

export const getMyTransports = (userId) => {
  return new Promise((resolve, reject) => {
    socket.emit("get_my_transports_webRTC", { userId }, (response) => {
      if (!response) {
        return reject(new Error("Aucune réponse du serveur"));
      }
      resolve(response);
    });
  });
};

export async function loadDevice(rtpCapabilities) {
  device = new mediasoupClient.Device();
  if (!device.loaded) {
    await device.load({ routerRtpCapabilities: rtpCapabilities });
  }
  return device;
}

export const sendTransportListenEvents = function (sendTransport) {
  // 1️⃣ Connexion du transport
  sendTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
    socket.emit(
      "connect_webRTC_transport",
      { transportId: sendTransport.id, dtlsParameters },
      (response) => {
        if (response.success) callback();
        else errback(response.error);
      }
    );
  });

  // 2️⃣ Production (très important ⚠️)
  sendTransport.on(
    "produce",
    async ({ kind, rtpParameters, appData }, callback, errback) => {
      try {
        socket.emit(
          "participant_produce",
          {
            transportId: sendTransport.id,
            kind,
            rtpParameters,
            appData: { ...appData, kind },
          },
          ({ id }) => {
            callback({ id }); // on donne l'id du producer au transport
          }
        );
      } catch (err) {
        errback(err);
      }
    }
  );

  // Changement d'état
  sendTransport.on("connectionstatechange", (state) => {
    if (state === "failed" || state === "disconnected")
      sendTransport.restartIce();
  });

  sendTransport.on("iceconnectionstatechange", (state) => {
    if (state === "failed" || state === "disconnected") {
      // Attendre un peu pour voir si ça se recouver automatiquement
      setTimeout(() => {
        if (sendTransport.iceConnectionState === "failed") {
          sendTransport.restartIce(); // Appel manuel seulement si nécessaire
        }
      }, 5000);
    }
  });
};

export const receiveTransportListenEvents = (datas) => {
  const { receiveTransport } = datas;
  if (!receiveTransport) return;
  receiveTransport.on("connect", ({ dtlsParameters }, callback, errBack) => {
    socket.emit(
      "connect_webRTC_transport",
      {
        transportId: receiveTransport.id,
        dtlsParameters,
      },
      (response) => {
        if (response.success) callback();
        else errBack(response.error);
      }
    );
  });

  // Surveillance de l'état de la connexion
  receiveTransport.on("connectionstatechange", (state) => {
    console.log(`État du recvTransport: ${state}`);
    if (state === "failed") receiveTransport.restartIce();
  });

  receiveTransport.on("iceconnectionstatechange", (state) => {
    if (state === "failed" || state === "disconnected") {
      // Attendre un peu pour voir si ça se recouver automatiquement
      setTimeout(() => {
        if (receiveTransport.iceConnectionState === "failed") {
          receiveTransport.restartIce(); // Appel manuel seulement si nécessaire
        }
      }, 5000);
    }
  });
};

export const rejoindreMeeting = async (data) => {
  return new Promise((resolve, reject) => {
    socket.emit("join_meeting", data, (response) => {
      if (!response) {
        return reject(new Error("Aucune réponse du serveur"));
      }
      resolve(response);
    });
  });
};

export async function consumeRemoteStream(
  groupedConsumer,
  recvTransport,
  setParticipants
) {
  const { userId, displayName, userImage, audio, video } = groupedConsumer;

  let consumer_audio = audio;
  let consumer_video = video;
  let remoteStream = new MediaStream();

  // --- 1️⃣ Consommer le flux audio ---
  if (audio) {
    consumer_audio = await recvTransport.consume({
      id: audio.id,
      producerId: audio.producerId,
      kind: audio.kind,
      rtpParameters: audio.rtpParameters,
      appData: audio.appData,
    });

    if (consumer_audio.track) {
      remoteStream.addTrack(consumer_audio.track);
    }

    // Reprendre le flux s’il est en pause côté serveur
    socket.emit("resume_consumer", { consumerId: consumer_audio.id }, (res) => {
      if (!res.success) {
        console.warn(`Erreur reprise audio du user ${userId}`);
      }
    });
  }

  // --- 2️⃣ Consommer le flux vidéo (si présent) ---
  if (video) {
    consumer_video = await recvTransport.consume({
      id: video.id,
      producerId: video.producerId,
      kind: video.kind,
      rtpParameters: video.rtpParameters,
      appData: video.appData,
    });

    if (consumer_video.track) {
      remoteStream.addTrack(consumer_video.track);
    }

    socket.emit("resume_consumer", { consumerId: consumer_video.id }, (res) => {
      if (!res.success) {
        console.warn(`Erreur reprise vidéo du user ${userId}`);
      }
    });
  }

  // --- 3️⃣ Mettre à jour ton état frontend (participants) ---
  if (consumer_audio) {
    upsertParticipant(setParticipants, userId, {
      userId,
      kind: "audio",
      mediaTag: audio.appData.mediaTag,
      isLocal: false,
      videoOn: video ? !video.producerPaused : false,
      audioOn: !audio.producerPaused,
      // consumers,
      stream: remoteStream,
      producerPaused: audio.producerPaused,
      displayName,
      userImage,
    });
  }

  if (consumer_video) {
    upsertParticipant(setParticipants, userId, {
      userId,
      kind: "video",
      mediaTag: video.appData.mediaTag,
      isLocal: false,
      videoOn: !video.producerPaused,
      audioOn: audio ? !audio.producerPaused : false,
      // consumers,
      stream: remoteStream,
      producerPaused: video.producerPaused,
      displayName,
      userImage,
    });
  }
}

export async function consumeRemoteStreamVideo(
  groupedConsumer,
  recvTransport,
  setParticipants,
  participants
) {
  const { userId, video } = groupedConsumer;
  let consumer_video = video;
  const { stream } = participants.find((peer) => peer.userId === userId);
  let remoteStream = stream;

  // --- 2️⃣ Consommer le flux vidéo (si présent) ---
  if (video) {
    consumer_video = await recvTransport.consume({
      id: video.id,
      producerId: video.producerId,
      kind: video.kind,
      rtpParameters: video.rtpParameters,
      appData: video.appData,
    });

    if (consumer_video.track) {
      remoteStream.addTrack(consumer_video.track);
    }

    socket.emit("resume_consumer", { consumerId: consumer_video.id }, (res) => {
      if (!res.success) {
        console.warn(`Erreur reprise vidéo du user ${userId}`);
      }
    });
  }

  if (consumer_video) {
    setParticipants((prev) =>
      prev.map((p) =>
        p.userId === userId
          ? {
              ...p,
              kind: "video",
              mediaTag: video.appData.mediaTag,
              audioOn: p.audioOn,
              videoOn: !video.producerPaused,
              producerPaused: video.producerPaused,
              stream: remoteStream,
            }
          : p
      )
    );
  }
}

export function groupConsumersByUser(consumers = []) {
  const grouped = consumers.reduce((acc, consumer) => {
    const userId = consumer?.appData?.userId;
    if (!userId || !consumer.kind) return acc; // skip entries invalides

    if (!acc[userId]) {
      acc[userId] = {
        userId,
        displayName: consumer.appData.displayName || "Unknown",
        userImage: consumer.appData.userImage || null,
        audio: null,
        video: null,
        streamsCount: 0,
      };
    }

    if (consumer.kind === "audio") {
      acc[userId].audio = consumer;
    } else if (consumer.kind === "video") {
      acc[userId].video = consumer;
    }

    acc[userId].streamsCount =
      (acc[userId].audio ? 1 : 0) + (acc[userId].video ? 1 : 0);

    return acc;
  }, {});

  return Object.values(grouped);
}

export const reconnectTransports = async (data) => {
  const {
    user,
    meetingId,
    accessCode,
    sendTransport,
    recvTransport,
    producerAudio,
    producerVideo,
    localStream,
    screenStream,
    setSendTransport,
    setRecvTransport,
    setProducerAudio,
    setProducerVideo,
    setIsScreenSharing,
    setScreenStream,
    setParticipants,
    startReconnection,
    endReconnection,
  } = data;

  try {
    startReconnection();

    // 1. Fermer les anciens transports et producers locaux
    await cleanupMediasoupConnection({
      sendTransport,
      recvTransport,
      producerVideo,
      producerAudio,
      localStream,
      screenStream,
    });

    // 2. Informer le serveur de la reconnexion (ferme les anciens transports serveur)
    const reconnectResponse = await new Promise((resolve) => {
      socket.emit("request_transport_recreation", { userId: user.userId }, resolve);
    });

    if (!reconnectResponse?.success) {
      throw new Error(reconnectResponse?.error || "Erreur de reconnexion serveur");
    }

    // 3. Créer de nouveaux transports sur le serveur
    const createResponse = await new Promise((resolve, reject) => {
      socket.emit(
        "create_all_webRTC_transports",
        { user, datas: { accessCode, meetingId } },
        (response) => {
          if (!response) return reject(new Error("Pas de réponse serveur"));
          if (response.error) return reject(new Error(response.error));
          resolve(response);
        }
      );
    });

    if (!createResponse.sendTransport || !createResponse.recvTransport) {
      throw new Error("Impossible de créer les transports serveur");
    }

    // 4. Récupérer les capacités RTP + paramètres des transports
    const { RTP_CAPABILITIES_SERVER } = await new Promise((resolve, reject) => {
      socket.emit("get_my_transports_webRTC", user.userId, (res) => {
        if (!res) return reject(new Error("Pas de réponse pour les transports"));
        resolve(res);
      });
    });

    // 5. Recréer le device avec les capacités RTP à jour
    const device = await loadDevice(RTP_CAPABILITIES_SERVER);

    // 6. Rejoindre à nouveau la réunion avec le nouveau device
    const joinResponse = await new Promise((resolve, reject) => {
      socket.emit("rejoin_meeting", { device, meetingId }, (res) => {
        if (!res || res.error) return reject(new Error(res?.error || "Erreur rejoin_meeting"));
        resolve(res);
      });
    });

    // 7. Créer les nouveaux transports côté client avec les params du serveur
    const iceServers = await getTurnServers();
    const newSendTransport = device.createSendTransport({
      ...createResponse.sendTransport,
      iceServers,
    });
    const newRecvTransport = device.createRecvTransport({
      ...createResponse.recvTransport,
      iceServers,
    });

    setSendTransport(newSendTransport);
    setRecvTransport(newRecvTransport);

    // 8. Configurer les listeners sur les nouveaux transports
    sendTransportListenEvents(newSendTransport);
    receiveTransportListenEvents({ receiveTransport: newRecvTransport });

    // 9. Re-produire depuis le flux local existant
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      const videoTrack = localStream.getVideoTracks()[0];

      if (audioTrack && audioTrack.readyState === "live") {
        const newProducerAudio = await newSendTransport.produce({
          track: audioTrack,
          appData: { mediaTag: "audio", userId: user.userId, userImage: user.userImage },
        });
        setProducerAudio(newProducerAudio);
      }

      if (videoTrack && videoTrack.readyState === "live") {
        const newProducerVideo = await newSendTransport.produce({
          track: videoTrack,
          appData: { mediaTag: "video", userId: user.userId, userImage: user.userImage, enabledVideo: true },
        });
        setProducerVideo(newProducerVideo);
      }
    }

    // 10. Consommer les producers existants des autres participants
    socket.emit("give_me_existing_consumers", null, async (consumers) => {
      if (!consumers || consumers.length === 0) return;
      for (const consumer of groupConsumersByUser(consumers)) {
        if (setParticipants) {
          await consumeRemoteStream(consumer, newRecvTransport, setParticipants);
        }
      }
    });

    endReconnection(true);
    return { success: true };
  } catch (error) {
    console.error("Erreur reconnexion:", error);
    endReconnection(false);
    return { success: false, error: error.message };
  }
};

export const setupTransportErrorHandlers = (transport, callbacks = {}) => {
  const {
    onConnectionStateChange,
    onIceConnectionStateChange,
    onReconnectNeeded,
    transportType,
  } = callbacks;

  transport.on("connectionstatechange", (state) => {
    console.log(`[${transportType}] Connection state: ${state}`);
    onConnectionStateChange?.(state);
    
    if (state === "failed" || state === "disconnected") {
      onReconnectNeeded?.();
    }
  });

  transport.on("iceconnectionstatechange", (state) => {
    console.log(`[${transportType}] ICE state: ${state}`);
    onIceConnectionStateChange?.(state);

    if (state === "failed" || state === "disconnected") {
      // Attendre un peu pour voir si ça se rectifie seul
      setTimeout(() => {
        if (transport.iceConnectionState === "failed" || 
            transport.iceConnectionState === "disconnected") {
          onReconnectNeeded?.();
        }
      }, 5000);
    }
  });
};
